export function getFavouriteSkillBucketLimits(config = null) {
  const configuredLimits = config?.favourite_skill_limits || {}

  return {
    core:
      typeof configuredLimits.core === 'number' && configuredLimits.core >= 0
        ? configuredLimits.core
        : 6,
    weapon:
      typeof configuredLimits.weapon === 'number' && configuredLimits.weapon >= 0
        ? configuredLimits.weapon
        : 4,
    magic:
      typeof configuredLimits.magic === 'number' && configuredLimits.magic >= 0
        ? configuredLimits.magic
        : 4,
  }
}

export function getFavouriteSkillBucket(skill, limits = getFavouriteSkillBucketLimits()) {
  if (!skill) return null

  const skillType = `${skill.type || ''}`.trim().toLowerCase()
  const skillCategory = `${skill.category || ''}`.trim().toLowerCase()

  if (skillType === 'melee' || skillType === 'ranged') {
    return {
      key: 'weapon',
      label: 'weapon skills',
      limit: limits.weapon,
      matches: (candidate) =>
        ['melee', 'ranged'].includes(`${candidate?.type || ''}`.trim().toLowerCase()),
    }
  }

  if (skillCategory === 'magic') {
    return {
      key: 'magic',
      label: 'casting skills',
      limit: limits.magic,
      matches: (candidate) => `${candidate?.category || ''}`.trim().toLowerCase() === 'magic',
    }
  }

  if (skillCategory === 'core') {
    return {
      key: 'core',
      label: 'core skills',
      limit: limits.core,
      matches: (candidate) => `${candidate?.category || ''}`.trim().toLowerCase() === 'core',
    }
  }

  return null
}

function getSkillComparisonKeys(skill) {
  const keys = []

  if (skill?.id !== undefined && skill?.id !== null && skill.id !== '') {
    keys.push(`id:${skill.id}`)
  }

  const displayName = `${skill?.display_name || ''}`.trim().toLowerCase()
  if (displayName) {
    keys.push(`display:${displayName}`)
  }

  const name = `${skill?.name || ''}`.trim().toLowerCase()
  if (name) {
    keys.push(`name:${name}`)
  }

  return keys
}

function findProjectedSkillIndex(skills, skill) {
  if (!Array.isArray(skills) || !skill) {
    return -1
  }

  const targetKeys = getSkillComparisonKeys(skill)
  if (!targetKeys.length) {
    return -1
  }

  return skills.findIndex((candidate) => {
    const candidateKeys = getSkillComparisonKeys(candidate)
    return candidateKeys.some((key) => targetKeys.includes(key))
  })
}

function normaliseSkillWithResolvedMeta(skill, resolveSkill = null) {
  if (!skill || typeof skill !== 'object') {
    return skill
  }

  const hasType = typeof skill.type === 'string' && skill.type.trim() !== ''
  const hasCategory = typeof skill.category === 'string' && skill.category.trim() !== ''

  if (hasType && hasCategory) {
    return skill
  }

  if (typeof resolveSkill !== 'function') {
    return skill
  }

  const resolved = resolveSkill(skill)

  if (!resolved || typeof resolved !== 'object') {
    return skill
  }

  return {
    ...skill,
    type: hasType ? skill.type : resolved.type,
    category: hasCategory ? skill.category : resolved.category,
  }
}

export function projectSkillsWithChangedFavourites(
  skills = [],
  changedSkills = [],
  resolveSkill = null,
) {
  const projectedSkills = Array.isArray(skills) ? skills.map((skill) => ({ ...skill })) : []

  if (!Array.isArray(changedSkills) || !changedSkills.length) {
    return projectedSkills.map((skill) => normaliseSkillWithResolvedMeta(skill, resolveSkill))
  }

  for (const change of changedSkills) {
    if (!change || typeof change !== 'object') {
      continue
    }

    if (change.action === 'DELETE') {
      const deleteIdx = findProjectedSkillIndex(projectedSkills, change)
      if (deleteIdx !== -1) {
        projectedSkills.splice(deleteIdx, 1)
      }
      continue
    }

    if (change.action === 'UPDATE') {
      const updateIdx = findProjectedSkillIndex(projectedSkills, change)
      if (updateIdx === -1) {
        continue
      }

      if (Object.prototype.hasOwnProperty.call(change.payload || {}, 'favourite')) {
        projectedSkills[updateIdx] = {
          ...projectedSkills[updateIdx],
          favourite: Boolean(change.payload.favourite),
        }
      }
      continue
    }

    if (change.action === 'ADD') {
      const resolvedSkill = typeof resolveSkill === 'function' ? resolveSkill(change) : null
      projectedSkills.push(
        normaliseSkillWithResolvedMeta(
          {
            id: change.id ?? null,
            name: change.name ?? resolvedSkill?.name ?? '',
            display_name: change.display_name ?? null,
            skill: change.payload?.skill ?? null,
            type: change.type ?? resolvedSkill?.type,
            category: change.category ?? resolvedSkill?.category,
            favourite: Boolean(change.payload?.favourite),
          },
          resolveSkill,
        ),
      )
    }
  }

  return projectedSkills.map((skill) => normaliseSkillWithResolvedMeta(skill, resolveSkill))
}

export function getFavouriteSkillBucketOverflow(skills = [], config = null, resolveSkill = null) {
  const limits = getFavouriteSkillBucketLimits(config)
  const favourites = Array.isArray(skills)
    ? skills
        .filter((skill) => Boolean(skill?.favourite))
        .map((skill) => normaliseSkillWithResolvedMeta(skill, resolveSkill))
    : []

  const buckets = [
    {
      key: 'weapon',
      label: 'weapon skills',
      limit: limits.weapon,
      matches: (candidate) =>
        ['melee', 'ranged'].includes(`${candidate?.type || ''}`.trim().toLowerCase()),
    },
    {
      key: 'magic',
      label: 'casting skills',
      limit: limits.magic,
      matches: (candidate) => `${candidate?.category || ''}`.trim().toLowerCase() === 'magic',
    },
    {
      key: 'core',
      label: 'core skills',
      limit: limits.core,
      matches: (candidate) => `${candidate?.category || ''}`.trim().toLowerCase() === 'core',
    },
  ]

  for (const bucket of buckets) {
    const count = favourites.filter((candidate) => bucket.matches(candidate)).length

    if (count > bucket.limit) {
      return {
        ...bucket,
        count,
      }
    }
  }

  return null
}
